import { config } from '@iheartjane/config';
import { DeliveryAddressFields } from '@iheartjane/menu';
import type { CountryOptions } from '@iheartjane/menu';
import { Flex, useMobileMediaQuery } from '@iheartjane/reefer';

import { useGetStore } from '@jane/shared/data-access';

import { selectCart } from '../../../../customer/redux/cart';
import { useCustomerSelector } from '../../../../customer/selectors';
import { useCartActions } from '../../cartFacade/useCartActions';
import { DeliveryAddressWrapper } from './cartDrawerAddress.styles';

interface CartDrawerAddressProps {
  addressErrorMessage: string;
  bloomUserExperience?: boolean;
  deliveryLabel: string;
  handleSetAddress: (address: string) => void;
  headless?: boolean;
  headlessWide?: boolean;
  initialDeliveryAddress: string;
  resetAddressValidation: () => void;
}

export const CartDrawerAddress = ({
  bloomUserExperience = false,
  deliveryLabel,
  initialDeliveryAddress,
  resetAddressValidation,
  addressErrorMessage,
  handleSetAddress,
  headless,
  headlessWide,
}: CartDrawerAddressProps) => {
  const isMobile =
    useMobileMediaQuery({}) && (!headless || !!bloomUserExperience);
  const { updateDeliveryAddress } = useCartActions();
  const { cart, deliveryAddress } = useCustomerSelector(selectCart);
  const { data: store } = useGetStore(cart?.store?.id);

  const countries = store?.country_code ? [store.country_code] : undefined;

  const handleApplyStreet2 = (street2: string) => {
    if (deliveryAddress?.street2 !== street2) {
      updateDeliveryAddress({
        deliveryAddress: { ...deliveryAddress, street2 },
      });
    }
  };

  const handleClearErrors = () => {
    if (addressErrorMessage) {
      resetAddressValidation();
    }
  };

  return (
    <DeliveryAddressWrapper headless={headless} headlessWide={headlessWide}>
      <Flex
        mt={!isMobile ? 8 : bloomUserExperience ? -16 : 0}
        mb={!isMobile ? 12 : bloomUserExperience ? 8 : 0}
        width="100%"
        gap={isMobile && bloomUserExperience ? 16 : undefined}
      >
        <DeliveryAddressFields
          addressErrorMessage={addressErrorMessage}
          addressLabel={`${deliveryLabel} address`}
          countries={countries as CountryOptions[]}
          defaultAddress={initialDeliveryAddress}
          defaultStreet2={deliveryAddress?.street2 || ''}
          disableMobileInputStyling={headless}
          googlePlacesKey={config.googlePlacesKey}
          onAddressChange={handleClearErrors}
          onAddressSelection={handleSetAddress}
          onStreet2Blur={handleApplyStreet2}
        />
      </Flex>
    </DeliveryAddressWrapper>
  );
};
